import { AutopodderResultRow, ScheduleDataItem } from "../types";

/**
 * Calculate how many times each ad is selected (only for current strategy's slots).
 */
export const getAdSelectionCounts = (
    poddedData: AutopodderResultRow[],
    slotSelectionDict: { [key: string]: string[] }
): { [scheduleName: string]: number } => {
    const counts: { [scheduleName: string]: number } = {};
    const currentStrategySlotIds = new Set(poddedData.map(slot => slot.ID));

    Object.entries(slotSelectionDict).forEach(([slotId, adNames]) => {
        if (currentStrategySlotIds.has(slotId)) {
            adNames.forEach((adName) => {
                counts[adName] = (counts[adName] || 0) + 1;
            });
        }
    });
    return counts;
};

/**
 * Calculate the filled length (total ad seconds) for a single slot.
 */
export const getFilledLengthForSlot = (
    recordId: string,
    slotSelectionDict: { [key: string]: string[] },
    scheduleData: { [key: string]: ScheduleDataItem }
): number => {
    const adNames = slotSelectionDict[recordId] || [];
    let totalLength = 0;
    adNames.forEach((adName) => {
        const scheduleItem = scheduleData[adName];
        if (scheduleItem && scheduleItem.UNITLENGTH) {
            totalLength += scheduleItem.UNITLENGTH;
        }
    });
    return totalLength;
};

/**
 * Calculate total dollar value for a single slot using actual rate (RATE_INT).
 */
export const getSlotDollarValue = (
    recordId: string,
    slotSelectionDict: { [key: string]: string[] },
    scheduleData: { [key: string]: ScheduleDataItem }
): number => {
    const adNames = slotSelectionDict[recordId] || [];
    let totalValue = 0;
    adNames.forEach((adName) => {
        const scheduleItem = scheduleData[adName];
        if (scheduleItem && scheduleItem.RATE_INT) {
            totalValue += scheduleItem.RATE_INT;
        }
    });
    return totalValue;
};

/**
 * Calculate total dollar value of all selected ads for the current strategy.
 */
export const calculateTotalDollarValue = (
    poddedData: AutopodderResultRow[],
    slotSelectionDict: { [key: string]: string[] },
    scheduleData: { [key: string]: ScheduleDataItem }
): number => {
    let total = 0;
    poddedData.forEach((slot) => {
        const adNames = slotSelectionDict[slot.ID] || [];
        adNames.forEach((adName) => {
            const scheduleItem = scheduleData[adName];
            if (scheduleItem && scheduleItem.RATE_INT) {
                total += scheduleItem.RATE_INT;
            }
        });
    });
    return total;
};

/**
 * Calculate total available ads time (sum of all selected ad lengths).
 */
export const calculateTotalAvailableAdsTime = (
    poddedData: AutopodderResultRow[],
    slotSelectionDict: { [key: string]: string[] },
    scheduleData: { [key: string]: ScheduleDataItem }
): number => {
    let total = 0;
    poddedData.forEach((slot) => {
        const adNames = slotSelectionDict[slot.ID] || [];
        adNames.forEach((adName) => {
            const scheduleItem = scheduleData[adName];
            if (scheduleItem && scheduleItem.UNITLENGTH) {
                total += scheduleItem.UNITLENGTH;
            }
        });
    });
    return total;
};

/**
 * Calculate total unfilled time across all slots (negative = overfilled).
 */
export const calculateTotalUnfilledTime = (
    poddedData: AutopodderResultRow[],
    slotSelectionDict: { [key: string]: string[] },
    scheduleData: { [key: string]: ScheduleDataItem }
): number => {
    let totalUnfilled = 0;
    poddedData.forEach((slot) => {
        const filledLength = getFilledLengthForSlot(slot.ID, slotSelectionDict, scheduleData);
        const maxLength = slot.SIZE_MAX;
        const unfilled = maxLength - filledLength;
        totalUnfilled += unfilled;
    });
    return totalUnfilled;
};

/**
 * Starting inventory for this podding run: CSV "Units Remaining" at load time,
 * stored as ORIGINAL_UNITS_REMAINING. Falls back to UNITS_CAP for legacy rows
 * written before ORIGINAL_UNITS_REMAINING was persisted.
 */
export const getStartingUnits = (record: ScheduleDataItem): number => {
    return record.ORIGINAL_UNITS_REMAINING ?? record.UNITS_CAP;
};

/**
 * Utilization as a percent, computed against starting inventory (not UNITS_CAP).
 */
export const calculateUtilization = (startingUnits: number, unitsRemaining: number): number => {
    if (startingUnits === 0) return 0;
    return Math.round(((startingUnits - unitsRemaining) / startingUnits) * 100);
};

/**
 * Normalize day-of-week string to 3-letter abbreviated form.
 */
export const normalizeDay = (day: string): string => {
    const dayLower = day.toLowerCase().trim();
    const dayMap: { [key: string]: string } = {
        'mon': 'Mon', 'monday': 'Mon',
        'tue': 'Tue', 'tuesday': 'Tue',
        'wed': 'Wed', 'wednesday': 'Wed',
        'thu': 'Thu', 'thursday': 'Thu',
        'fri': 'Fri', 'friday': 'Fri',
        'sat': 'Sat', 'saturday': 'Sat',
        'sun': 'Sun', 'sunday': 'Sun',
    };
    return dayMap[dayLower] || day;
};

/**
 * Get day-of-week usage count for an ad considering current selections.
 */
export const getDayOfWeekUsageCount = (
    adName: string,
    dayOfWeek: string,
    poddedData: AutopodderResultRow[],
    slotSelectionDict: { [key: string]: string[] }
): number => {
    const normalizedDay = normalizeDay(dayOfWeek);

    let count = 0;
    poddedData.forEach((slot) => {
        const slotDay = normalizeDay(slot.DAY_OF_WEEK || '');
        if (slotDay === normalizedDay) {
            const slotSelections = slotSelectionDict[slot.ID] || [];
            slotSelections.forEach((selectedAd) => {
                if (selectedAd === adName) {
                    count++;
                }
            });
        }
    });

    return count;
};
